/**
 * ngPrometFilters — the vocabulary of the two filters of the zone view »Promet
 * po conah« (#740; Priloga 1 §4.1): traffic categories and weekdays. One pure
 * module for the server (query, SQL class list, labels, notes, exports, file
 * names) and the client (the form, the map label's unit), so a class or a day
 * is named in exactly one place.
 *
 * The categories are the five classes the counters deliver (Telraam's basic
 * report). Three facts shape every sentence here:
 *  - `night` is not a vehicle class and not a fixed clock band. It is what the
 *    counter detects in darkness by its lights, without telling road users
 *    apart — »mainly motorised vehicles, but also some bicycles with stronger
 *    headlights« (Telraam, API introduction). About 21–05 in July, 17–07 in
 *    December; `car` and `heavy` read ≈ 0 then (gsx8_dev, cover sensors,
 *    checked 6. 10. 2026).
 *  - `bike` is TWO-WHEELERS — »mainly cyclists and motorbikes« in Telraam's
 *    API specification — never cyclists alone. Bicycles on their own exist only
 *    in the advanced report (`mode_bicycle_*`), which this product does not read.
 *  - two-wheelers and pedestrians are counted in daylight only (same check).
 */

export const PROMET_CATEGORIES = ['car', 'heavy', 'night', 'bike', 'pedestrian'] as const;
export type PrometCategory = (typeof PROMET_CATEGORIES)[number];

/** Today's »motorni promet« (Priloga 1 §3) — what a request without the filter counts. */
export const DEFAULT_PROMET_CATEGORIES: readonly PrometCategory[] = ['car', 'heavy', 'night'];

/** ISO weekdays: 1 = Monday … 7 = Sunday. */
export const ALL_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

const MOTOR: ReadonlySet<PrometCategory> = new Set<PrometCategory>(['car', 'heavy', 'night']);

const CATEGORY_WORDS_SL: Record<PrometCategory, string> = {
  car: 'osebna vozila',
  heavy: 'tovorna vozila',
  night: 'nočni promet',
  bike: 'dvokolesa',
  pedestrian: 'pešci',
};

/** The words beside a checkbox: the class, and what it really holds where the short word is not enough. */
const CATEGORY_FORM_LABELS_SL: Record<PrometCategory, string> = {
  car: 'osebna vozila',
  heavy: 'tovorna vozila',
  night: 'nočni promet (v temi)',
  bike: 'dvokolesa (kolesa in motorna kolesa)',
  pedestrian: 'pešci',
};

/** ASCII codes for a file name. */
const CATEGORY_FILE_CODES: Record<PrometCategory, string> = {
  car: 'os',
  heavy: 'tov',
  night: 'noc',
  bike: 'dvo',
  pedestrian: 'pes',
};

const WEEKDAY_SHORT_SL = ['pon', 'tor', 'sre', 'čet', 'pet', 'sob', 'ned'];
const WEEKDAY_FILE_CODES = ['pon', 'tor', 'sre', 'cet', 'pet', 'sob', 'ned'];
const WEEKDAY_NAMES_SL = ['ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota', 'nedelja'];

// ---------------------------------------------------------------------------
// Canonical lists
// ---------------------------------------------------------------------------

function isCategory(value: string): value is PrometCategory {
  return (PROMET_CATEGORIES as readonly string[]).includes(value);
}

/** The known classes of `list`, once each, in the canonical order. */
export function normalizeCategories(list: readonly string[]): PrometCategory[] {
  return PROMET_CATEGORIES.filter((c) => list.includes(c));
}

/** The days 1–7 of `list`, once each, ascending. */
export function normalizeWeekdays(list: readonly number[]): number[] {
  return ALL_WEEKDAYS.filter((d) => list.includes(d));
}

export function isDefaultCategories(list: readonly PrometCategory[]): boolean {
  const n = normalizeCategories(list);
  return n.length === DEFAULT_PROMET_CATEGORIES.length && n.every((c, i) => c === DEFAULT_PROMET_CATEGORIES[i]);
}

/** Every day chosen = no weekday filter. */
export function isAllWeekdays(list: readonly number[]): boolean {
  return normalizeWeekdays(list).length === ALL_WEEKDAYS.length;
}

// ---------------------------------------------------------------------------
// Query parameter ⇄ list
// ---------------------------------------------------------------------------

function tokens(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/**
 * `categories=car,heavy` → the classes in canonical order. Throws on an
 * unknown class or an empty list: a filter that cannot be read must not fall
 * back to »everything« without a word.
 */
export function parseCategoriesParam(raw: string): PrometCategory[] {
  const parts = tokens(raw);
  if (parts.length === 0) throw new Error('categories is empty');
  const unknown = parts.find((p) => !isCategory(p));
  if (unknown !== undefined) {
    throw new Error(`categories has an unknown class: ${unknown} (known: ${PROMET_CATEGORIES.join(', ')})`);
  }
  return normalizeCategories(parts);
}

/** `weekdays=6,7` → ISO days ascending. Throws on anything but 1–7, or an empty list. */
export function parseWeekdaysParam(raw: string): number[] {
  const parts = tokens(raw);
  if (parts.length === 0) throw new Error('weekdays is empty');
  const bad = parts.find((p) => !/^[1-7]$/.test(p));
  if (bad !== undefined) throw new Error(`weekdays must be days 1–7 (1 = Monday): ${bad}`);
  return normalizeWeekdays(parts.map(Number));
}

/** The query value of a category choice; null = the default, which is not sent. */
export function categoriesParam(list: readonly PrometCategory[]): string | null {
  return isDefaultCategories(list) ? null : normalizeCategories(list).join(',');
}

/** The query value of a weekday choice; null = every day, which is not sent. */
export function weekdaysParam(list: readonly number[]): string | null {
  return isAllWeekdays(list) ? null : normalizeWeekdays(list).join(',');
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export function categoryLabel(category: PrometCategory): string {
  return CATEGORY_WORDS_SL[category];
}

/** »dvokolesa (kolesa in motorna kolesa)« — the label of the class's checkbox. */
export function categoryFormLabel(category: PrometCategory): string {
  return CATEGORY_FORM_LABELS_SL[category];
}

/** »osebna vozila, tovorna vozila, nočni promet« */
export function categoryWords(list: readonly PrometCategory[]): string {
  return normalizeCategories(list).map((c) => CATEGORY_WORDS_SL[c]).join(', ');
}

/** 6 → »sob« */
export function weekdayShort(isoDay: number): string {
  return WEEKDAY_SHORT_SL[isoDay - 1] ?? '';
}

/** 3 → »sreda« */
export function weekdayName(isoDay: number): string {
  return WEEKDAY_NAMES_SL[isoDay - 1] ?? '';
}

/** »sob, ned« */
export function weekdayWords(list: readonly number[]): string {
  return normalizeWeekdays(list).map(weekdayShort).join(', ');
}

/** »vsi« for every day, else »sob, ned« — the form an export prints in any case. */
export function weekdayWordsOrAll(list: readonly number[]): string {
  return isAllWeekdays(list) ? 'vsi' : weekdayWords(list);
}

/**
 * The unit of a count: vehicles only while every chosen class is motor
 * traffic. A sum that holds cyclists or pedestrians gets no unit word.
 */
export function prometUnit(list: readonly PrometCategory[]): string {
  return list.length > 0 && list.every((c) => MOTOR.has(c)) ? 'voz.' : '';
}

/** »Kategorije: dvokolesa, pešci · Dnevi v tednu: sob, ned« — only the filters in use; null for none. */
export function filterLine(categories: readonly PrometCategory[], weekdays: readonly number[]): string | null {
  const parts: string[] = [];
  if (!isDefaultCategories(categories)) parts.push(`Kategorije: ${categoryWords(categories)}`);
  if (!isAllWeekdays(weekdays)) parts.push(`Dnevi v tednu: ${weekdayWords(weekdays)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** `-kat_dvo+pes-dni_sob+ned` for a file name (ASCII); '' without a filter. */
export function filterFileMarker(categories: readonly PrometCategory[], weekdays: readonly number[]): string {
  let out = '';
  if (!isDefaultCategories(categories)) {
    out += `-kat_${normalizeCategories(categories).map((c) => CATEGORY_FILE_CODES[c]).join('+')}`;
  }
  if (!isAllWeekdays(weekdays)) {
    out += `-dni_${normalizeWeekdays(weekdays).map((d) => WEEKDAY_FILE_CODES[d - 1]).join('+')}`;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Notes (panel footnotes, Excel »Obdobje«, SHP opis_polj.txt)
// ---------------------------------------------------------------------------

/**
 * What a category choice other than the default counts, in sentences: the
 * classes, and what the counter does in darkness for each kind chosen. Empty
 * for the default — the standing note »Motorni promet = …« covers it.
 */
export function categoryNotes(list: readonly PrometCategory[]): string[] {
  if (isDefaultCategories(list)) return [];
  const chosen = normalizeCategories(list);
  const has = (c: PrometCategory): boolean => chosen.includes(c);
  const notes = [`Prikazane kategorije: ${categoryWords(chosen)}.`];
  if (has('night')) {
    notes.push(
      'Nočni promet so udeleženci, ki jih števec zazna v temi po lučeh: večinoma motorna vozila, lahko tudi ' +
        'kolesa z močno lučjo. V temi števec ne loči vrst (poleti približno med 21. in 5. uro, pozimi med 17. in ' +
        '7. uro).',
    );
  } else if (has('car') || has('heavy')) {
    const what = has('car') && has('heavy') ? 'Osebna in tovorna vozila' : has('car') ? 'Osebna vozila' : 'Tovorna vozila';
    notes.push(
      `${what} števec loči samo ob dnevni svetlobi; promet v temi je v kategoriji »nočni promet«, ki ni izbrana.`,
    );
  }
  if (has('bike')) {
    notes.push(
      'Dvokolesa so kolesa in motorna kolesa skupaj; števec ju ne loči, kot dvokolesa pa jih šteje samo ob dnevni svetlobi.',
    );
  }
  if (has('pedestrian')) notes.push('Pešce števec šteje samo ob dnevni svetlobi.');
  return notes;
}

/** The note of a weekday choice; null when every day counts. */
export function weekdayNote(list: readonly number[]): string | null {
  if (isAllWeekdays(list)) return null;
  return (
    `Upoštevani so samo izbrani dnevi v tednu: ${weekdayWords(list)}. ` +
    'Tudi pravilo polovice dni šteje samo te dneve.'
  );
}

/**
 * A P of one day (or one hour) is the day the user named and is never
 * filtered; this note says so when its weekday is not among the chosen ones.
 */
export function exactComparisonNote(dayLabel: string, isoDay: number): string {
  return (
    `Obdobje P (${weekdayName(isoDay)}, ${dayLabel}) ni med izbranimi dnevi v tednu; ` +
    'izbira dni velja za referenčno obdobje R.'
  );
}

// ---------------------------------------------------------------------------
// The filters of an answer
// ---------------------------------------------------------------------------

/**
 * The filters an answer was built with (`applied` of the zones response). An
 * answer from a server before #740 names neither list: it counted the
 * defaults, so the defaults are what this returns.
 */
export function appliedFilters(
  applied: { categories?: readonly string[]; weekdays?: readonly number[] } | null | undefined,
): { categories: PrometCategory[]; weekdays: number[] } {
  const categories = applied?.categories ? normalizeCategories(applied.categories) : [];
  const weekdays = applied?.weekdays ? normalizeWeekdays(applied.weekdays) : [];
  return {
    categories: categories.length > 0 ? categories : [...DEFAULT_PROMET_CATEGORIES],
    weekdays: weekdays.length > 0 ? weekdays : [...ALL_WEEKDAYS],
  };
}
